import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    BadgeCheck, CheckCircle2, FileText, Loader2, PencilRuler, Ruler, ShieldCheck, Upload, X,
} from "lucide-react";
import Layout from "@/components/layout/Layout";
import { CrumbBar } from "@/components/layout/Crumbs";
import api, { formatApiError } from "@/lib/api";
import { useMall } from "@/context/MallContext";
import { useAuth } from "@/context/AuthContext";
import { themeOf, tint } from "@/lib/mall";
import { Reveal } from "@/components/Reveal";
import { StoreButton } from "@/components/Buttons";
import { JobCards } from "@/components/artwork/JobCards";
import { Lessons } from "@/components/artwork/Lessons";
import { PageBanner } from "@/components/mall/PageBanner";

const input = "w-full rounded-xl border border-white/15 bg-brand-ink px-4 py-3 text-white placeholder:text-zinc-500 focus:outline-none";
const VERDICT = { best: "text-brand-green", good: "text-brand-blue", ok: "text-amber-300", careful: "text-red-300" };

export default function ArtworkRequest() {
    const { store, homeStore } = useMall();
    const { user } = useAuth();
    const shop = store || homeStore;
    const theme = themeOf(shop);
    const fileRef = useRef();
    const placeRef = useRef();

    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "", description: "", date_needed: "" });
    const [products, setProducts] = useState([]);
    const [hasArt, setHasArt] = useState(null);
    const [service, setService] = useState("");
    const [files, setFiles] = useState([]);
    const [placement, setPlacement] = useState([]);
    const [uploading, setUploading] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => { api.get("/artwork/config").then(({ data }) => setCfg(data)).catch(() => setCfg(null)); }, []);
    useEffect(() => { if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" })); }, [user]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const pick = (name) => setProducts((p) => (p.includes(name) ? p.filter((x) => x !== name) : [...p, name]));

    const upload = async (list, kind) => {
        setUploading(kind);
        try {
            const out = [];
            for (const f of list) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                out.push(data);
            }
            (kind === "art" ? setFiles : setPlacement)((p) => [...p, ...out]);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setUploading(""); }
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (!products.length) { toast.error("Pick what you're making"); return; }
        if (hasArt === null) { toast.error("Do you already have artwork?"); return; }
        if (!service) { toast.error("Pick what you need — check, cleanup or rebuild"); return; }
        if (!form.name || !form.email || !form.description) { toast.error("Name, email and a description please"); return; }
        setBusy(true);
        try {
            const tier = (cfg?.tiers || []).find((t) => t.key === service);
            await api.post("/artwork-requests", {
                ...form, files, placement, products, has_artwork: hasArt, service,
                service_label: tier ? `${tier.name} (${tier.price})` : service,
                store_slug: shop?.slug || null,
            });
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (done) return (
        <Layout hideTicker>
            <div className="mx-auto max-w-xl px-4 py-24 text-center">
                <CheckCircle2 size={48} className="mx-auto" style={{ color: theme.ink || theme.primary }} />
                <h1 className="mt-5 font-display text-3xl font-black tracking-tight">The art room has it.</h1>
                <p className="mt-3 text-zinc-300" data-testid="artwork-goes-to">
                    {shop?.name || "13th Ave"} got your files and your job list. You'll get a receipt by email,
                    then a real answer from the department — pass, fail, or here's the quote.
                </p>
                <Link to="/directory" className="mt-7 inline-block"><StoreButton theme={theme}>Back to the directory</StoreButton></Link>
            </div>
        </Layout>
    );

    const jobs = cfg?.jobs || [];

    return (
        <Layout hideTicker>
            <PageBanner room="artwork" />
            <CrumbBar trail={[{ label: "Artwork request" }]} />
            <section className="dark-block relative overflow-hidden border-b border-white/10">
                {cfg?.hero_image && (
                    <>
                        <img src={cfg.hero_image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
                        <span className="absolute inset-0" style={{ background: `linear-gradient(100deg, #0B0B0B 20%, rgba(11,11,11,0.75) 60%, ${tint(theme.primary, 0.2)})` }} />
                    </>
                )}
                <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6">
                    <Reveal>
                        <p className="inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-[0.24em]"
                            style={{ borderColor: tint(theme.primary, 0.45), color: theme.primary, background: tint(theme.primary, 0.08) }}
                            data-testid="artwork-destination">
                            <PencilRuler size={12} /> Artwork Department · goes to {shop?.name || "13th Ave"}
                        </p>
                        <h1 className="mt-6 font-display text-4xl font-black leading-[1.03] tracking-tight sm:text-5xl lg:text-6xl">
                            ARTWORK<br />DEPARTMENT.
                        </h1>
                        <p className="mt-5 max-w-2xl text-base text-zinc-300 md:text-lg">
                            We make the file print-ready for whatever you're buying on this site. Not a logo contest,
                            not a design agency — a print department that sets your art up for the exact job so it
                            comes off the machine clean.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href="#request"><StoreButton theme={theme}><Upload size={16} /> Request Artwork</StoreButton></a>
                            <a href="#learn" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-bold uppercase tracking-wide text-zinc-200 transition-colors hover:border-white/50 hover:text-white">
                                Good file vs bad file
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* the jobs */}
            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
                <Reveal>
                    <h2 className="font-display text-3xl font-black tracking-tight sm:text-4xl">What are you making?</h2>
                    <p className="mt-2 max-w-2xl text-base text-zinc-400">
                        Every job wants a different file. Here's what "print-ready" means for each thing this shop sells.
                    </p>
                </Reveal>
                <div className="mt-8"><JobCards jobs={jobs} theme={theme} picked={products} onPick={pick} /></div>
            </section>

            {/* lessons */}
            <section id="learn" className="border-y border-white/10 bg-white/[0.02]">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
                            The five things that ruin a print.
                        </h2>
                        <p className="mt-2 max-w-2xl text-base text-zinc-400">
                            Not a textbook. Look at the picture, read the one rule, send us better files.
                        </p>
                    </Reveal>
                    <div className="mt-10"><Lessons lessons={cfg?.lessons || []} theme={theme} /></div>
                </div>
            </section>

            {/* file types */}
            <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
                <Reveal>
                    <h2 className="flex items-center gap-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
                        <FileText /> File types, in normal English.
                    </h2>
                </Reveal>
                <div className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="artwork-file-types">
                    {(cfg?.file_types || []).map((f) => (
                        <div key={f.ext} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                            <p className="flex items-baseline justify-between gap-3">
                                <span className="font-mono text-sm font-black tracking-[0.1em]">{f.ext}</span>
                                <span className={`font-mono text-[11px] uppercase tracking-[0.2em] ${VERDICT[f.verdict] || "text-zinc-400"}`}>
                                    {f.verdict}
                                </span>
                            </p>
                            <p className="mt-2 text-sm text-zinc-300">{f.plain}</p>
                        </div>
                    ))}
                </div>

                {/* sizes + trim */}
                <div className="mt-10 grid items-center gap-8 lg:grid-cols-2">
                    {cfg?.trim?.image && (
                        <img src={cfg.trim.image} alt="Banner trim marks" loading="lazy"
                            className="w-full rounded-3xl border object-cover" style={{ borderColor: tint(theme.primary, 0.3) }} />
                    )}
                    <div data-testid="artwork-trim">
                        <h3 className="flex items-center gap-2.5 font-display text-2xl font-black tracking-tight">
                            <Ruler /> Sizes, bleed and the cut
                        </h3>
                        <ul className="mt-4 space-y-2.5">
                            {(cfg?.trim?.rules || []).map((r) => (
                                <li key={r} className="flex gap-3 text-base text-zinc-300">
                                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: theme.primary }} /> {r}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* promise */}
            <section className="border-y border-white/10" style={{ background: tint(theme.primary, 0.06) }}>
                <div className="mx-auto flex max-w-4xl flex-col items-start gap-4 px-4 py-14 sm:px-6" data-testid="artwork-promise">
                    <p className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em]" style={{ color: theme.ink || theme.primary }}>
                        <ShieldCheck size={13} /> The department's promise
                    </p>
                    <p className="font-display text-2xl font-black leading-snug tracking-tight sm:text-3xl">{cfg?.promise}</p>
                </div>
            </section>

            {/* pricing + form */}
            <section id="request" className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
                <Reveal>
                    <h2 className="font-display text-3xl font-black tracking-tight sm:text-4xl">Pick what you need.</h2>
                    <p className="mt-2 text-base text-zinc-400">Honest prices. No free unlimited design, no surprises.</p>
                </Reveal>

                <div className="mt-8 grid gap-4 lg:grid-cols-3" data-testid="artwork-tiers">
                    {(cfg?.tiers || []).map((t) => {
                        const on = service === t.key;
                        return (
                            <button key={t.key} type="button" onClick={() => setService(t.key)} data-testid={`artwork-tier-${t.key}`}
                                className="flex h-full flex-col rounded-3xl border p-6 text-left transition-transform duration-200 hover:-translate-y-1"
                                style={{ borderColor: on ? theme.primary : "rgba(0,0,0,0.10)", background: on ? tint(theme.primary, 0.08) : "#FFFFFF" }}>
                                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-500">{t.name}</p>
                                <p className="mt-3 font-display text-3xl font-black tracking-tight" style={{ color: on ? (theme.ink || theme.primary) : undefined }}>{t.price}</p>
                                <p className="mt-3 text-sm text-zinc-600">{t.blurb}</p>
                                <ul className="mt-4 flex-1 space-y-1.5">
                                    {t.includes.map((i) => (
                                        <li key={i} className="flex gap-2 text-sm text-zinc-600">
                                            <BadgeCheck size={15} className="mt-0.5 shrink-0" style={{ color: theme.ink || theme.primary }} /> {i}
                                        </li>
                                    ))}
                                </ul>
                                <span className="mt-5 inline-block rounded-full border px-4 py-2 text-center font-mono text-[11px] uppercase tracking-[0.18em]"
                                    style={{ borderColor: on ? theme.primary : "rgba(0,0,0,0.18)", color: on ? (theme.ink || theme.primary) : "#52525b" }}>
                                    {on ? "selected" : "choose this"}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <form onSubmit={submit} className="mt-10 space-y-6 rounded-3xl border p-6 sm:p-8" data-testid="artwork-request-form"
                    style={{ borderColor: tint(theme.primary, 0.3), background: tint(theme.primary, 0.03) }}>
                    <div>
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">What are you making? *</p>
                        <div className="mt-3 flex flex-wrap gap-2" data-testid="artwork-product-picker">
                            {jobs.map((j) => (
                                <button key={j.key} type="button" onClick={() => pick(j.name)} data-testid={`artwork-product-${j.key}`}
                                    className="rounded-full border px-4 py-2 text-sm transition-colors"
                                    style={products.includes(j.name)
                                        ? { borderColor: theme.primary, color: theme.ink || theme.primary, background: tint(theme.primary, 0.1) }
                                        : { borderColor: "rgba(0,0,0,0.14)", color: "#3f3f46" }}>
                                    {j.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Do you already have artwork? *</p>
                            <div className="mt-3 flex gap-2">
                                {[["Yes", true], ["No", false]].map(([label, val]) => (
                                    <button key={label} type="button" onClick={() => setHasArt(val)} data-testid={`artwork-has-${label.toLowerCase()}`}
                                        className="flex-1 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors"
                                        style={hasArt === val
                                            ? { borderColor: theme.primary, color: theme.ink || theme.primary, background: tint(theme.primary, 0.1) }
                                            : { borderColor: "rgba(0,0,0,0.14)", color: "#3f3f46" }}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <label className="block">
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Deadline</p>
                            <input type="date" className={`${input} mt-3`} value={form.date_needed} onChange={set("date_needed")} data-testid="art-date" />
                        </label>
                    </div>

                    {hasArt !== false && (
                        <div>
                            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading === "art"} data-testid="art-upload"
                                className="flex w-full flex-col items-center gap-2 rounded-2xl border-2 border-dashed py-7 transition-colors"
                                style={{ borderColor: tint(theme.primary, 0.35) }}>
                                {uploading === "art" ? <Loader2 className="animate-spin" style={{ color: theme.ink || theme.primary }} /> : <Upload style={{ color: theme.ink || theme.primary }} />}
                                <span className="text-sm font-semibold">Upload your artwork — AI, EPS, PDF, PSD, PNG, JPG</span>
                                <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500">as many files as you've got</span>
                            </button>
                            <input ref={fileRef} type="file" multiple className="hidden" accept="image/*,.pdf,.ai,.eps,.psd,.svg"
                                onChange={(e) => upload(Array.from(e.target.files), "art")} />
                            {!!files.length && (
                                <div className="mt-3 space-y-2" data-testid="art-files">
                                    {files.map((f, i) => (
                                        <div key={i} className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2 text-sm">
                                            <span className="truncate">{f.filename}</span>
                                            <button type="button" onClick={() => setFiles(files.filter((_, x) => x !== i))} className="text-zinc-500 hover:text-red-400"><X size={15} /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <textarea className={input} rows={5} data-testid="art-description" value={form.description} onChange={set("description")}
                        placeholder="Describe the job — size, colors, the text word for word, where it goes on the shirt or sign, and when you need it." />

                    <div>
                        <button type="button" onClick={() => placeRef.current?.click()} disabled={uploading === "place"} data-testid="art-placement-upload"
                            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-300 hover:border-white/50 hover:text-white">
                            {uploading === "place" ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} optional: photo of the shirt or sign for placement
                        </button>
                        <input ref={placeRef} type="file" multiple className="hidden" accept="image/*"
                            onChange={(e) => upload(Array.from(e.target.files), "place")} />
                        {!!placement.length && (
                            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.16em]" style={{ color: theme.ink || theme.primary }} data-testid="art-placement-files">
                                {placement.length} placement photo{placement.length > 1 ? "s" : ""} attached
                            </p>
                        )}
                    </div>

                    <div className="grid gap-4 sm:grid-cols-3">
                        <input className={input} placeholder="Name *" value={form.name} onChange={set("name")} data-testid="art-name" />
                        <input className={input} placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid="art-email" />
                        <input className={input} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="art-phone" />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6">
                        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                            {service ? `you picked: ${(cfg?.tiers || []).find((t) => t.key === service)?.name}` : "pick a service above"}
                            {products.length ? ` · ${products.length} job${products.length > 1 ? "s" : ""}` : ""}
                        </p>
                        <StoreButton theme={theme} type="submit" disabled={busy} data-testid="art-submit">
                            {busy ? <Loader2 className="animate-spin" size={16} /> : <PencilRuler size={16} />} Send it to the art room
                        </StoreButton>
                    </div>
                </form>
            </section>
        </Layout>
    );
}
